import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    FlexBox,
    FormField,
    FormInputGroup,
    Icon,
    StandardForm,
    TextInput,
} from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrackNavigationClicked } from '../../../../utils/analytics';
import { logomakerCtaPathByLocale } from '../../../../utils/urls';
import styles, { stylesheet } from './HeroBanner.module.scss';

interface BusinessNameInputProps {
    setIsPaused: (isPaused: boolean) => void;
    pauseButtonRef: React.RefObject<HTMLButtonElement | null>;
}

const BusinessNameInput = ({ setIsPaused, pauseButtonRef }: BusinessNameInputProps) => {
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const [businessName, setBusinessName] = useState('');
    const logomakerGenAiPath = logomakerCtaPathByLocale(locale);
    const logomakerCTARef = useTrackNavigationClicked(
        'Design Services',
        'Hero Banner CTA',
        logomakerGenAiPath,
        'logoMaker',
    );

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setBusinessName(e.target.value);
    };

    const handleOnFocus = () => {
        setIsPaused(true);
    };

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Don't resume if user is clicking the pause button - let the button handle it
        if (e.relatedTarget === pauseButtonRef.current) {
            return;
        }
        setIsPaused(false);
    };

    const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Short delay before redirecting to allow tracking event to be fired.
        setTimeout(() => {
            // TODO: replace with new Contentful string for Logomaker GenAI page
            window.location.href = `${logomakerGenAiPath}?businessName=${encodeURIComponent(businessName)}`;
        }, 300);
    };

    useStyles(stylesheet);

    return (
        <StandardForm onSubmit={handleOnSubmit} className={styles.standardForm}>
            <FlexBox flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
                <FormField style={{ flexGrow: 1 }}>
                    <FormInputGroup marginBottom="0">
                        <TextInput
                            name="businessName"
                            placeholder={t(
                                'Logomaker GenAi Landing Page > Hero > Input > Placeholder.value',
                            )}
                            aria-label={t(
                                'Logomaker GenAi Landing Page > Hero > Input > Aria Label.value',
                            )}
                            value={businessName}
                            onChange={handleOnChange}
                            onFocus={handleOnFocus}
                            onBlur={handleOnBlur}
                        />
                    </FormInputGroup>
                </FormField>
                <Box
                    marginLeft={{ xs: 0, sm: 'between-actions' }}
                    marginTop={{ xs: 4, sm: 0 }}
                    style={{ flexShrink: 0 }}
                >
                    <Button
                        id="hero-cta"
                        skin="primary"
                        textAlign="center"
                        type="submit"
                        ref={logomakerCTARef}
                        iconPosition="left"
                    >
                        <Icon alt="next step" iconType="magic" />
                        {t('Logomaker GenAi Landing Page > Hero > CTA > Text.value')}
                    </Button>
                </Box>
            </FlexBox>
        </StandardForm>
    );
};

export default BusinessNameInput;
